import React from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  Button,
  Alert,
} from "@mui/material";
import OrbLoader from "../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import {
  useGetMarginsByInternalCampaignIdQuery,
  useGetRawMarginsByInternalCampaignIdQuery,
} from "../store/apis/metricsApi";
import { useGetInternalCampaignByIdQuery } from "../store/apis/internalCampaignApi";
import MarginTable from "../components/table/MarginTable";
import InternalCampaignDbMarginTable from "../components/table/InternalCampaignDbMarginTable";
import dayjs from "dayjs";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import { useGoBack } from "../hooks/useGoBack";

interface LocationState {
  internalCampaignName?: string;
}

const InternalCampaignMargins: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const goBack = useGoBack();
  const { internalCampaignId } = useParams<{ internalCampaignId: string }>();
  const location = useLocation();
  const { internalCampaignName: nameFromLocation } =
    (location.state as LocationState) || {};
  const collapsed = useSelector(selectSidebarCollapsed);

  // Fetch internal campaign details
  const {
    data: internalCampaign,
    isLoading: isLoadingCampaign,
    error: campaignError,
  } = useGetInternalCampaignByIdQuery(internalCampaignId || "", {
    skip: !internalCampaignId,
  });

  // Extract campaign details
  const campaignData = internalCampaign?.data;
  const internalCampaignName = nameFromLocation || campaignData?.displayName;
  const campaignStartDate = campaignData?.startDate || null;
  const campaignEndDate = campaignData?.endDate || null;

  const handleBack = () => {
    goBack("/metrics");
  };

  // Fetch margin data for this internal campaign
  const {
    data: marginPeriods,
    isLoading: isLoadingMargins,
    error: marginError,
  } = useGetMarginsByInternalCampaignIdQuery(
    { id: internalCampaignId || "" },
    { skip: !internalCampaignId }
  );

  // Fetch raw margin data from the database
  const {
    data: rawMargins,
    isLoading: isLoadingRawMargins,
    error: rawMarginError,
  } = useGetRawMarginsByInternalCampaignIdQuery(
    { id: internalCampaignId || "" },
    { skip: !internalCampaignId }
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box>
            <Typography variant="h3" gutterBottom>
              {t(
                "metrics.internalCampaignMargins",
                "Internal Campaign Margins"
              )}
            </Typography>
            <Typography
              variant="pSmall"
              gutterBottom
              color={theme.palette.neutral["600"]}
            >
              {campaignStartDate && campaignEndDate
                ? `${dayjs(campaignStartDate).format("LL")} - ${dayjs(
                    campaignEndDate
                  ).format("LL")}`
                : dayjs().format("LL")}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            mt: 2,
            mb: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
            sx={{
              borderRadius: 50,
              textTransform: "none",
              fontFamily: "Nunito",
              borderColor: theme.palette.grey[200],
              color: theme.palette.grey[700],
            }}
          >
            {t("common.back", "Back")}
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="h4" sx={{ mb: 3 }}>
            {internalCampaignName
              ? t("metrics.internalCampaignName", "{{name}}", {
                  name: internalCampaignName,
                })
              : t(
                  "metrics.internalCampaignId",
                  "Internal Campaign ID: {{id}}",
                  {
                    id: internalCampaignId,
                  }
                )}
          </Typography>

          <Typography variant="h5" sx={{ mb: 2 }}>
            {t("metrics.calculatedMargins", "Calculated Margins")}
          </Typography>

          {isLoadingMargins ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <OrbLoader
                size="large"
                label={t("common.loading", { defaultValue: "Cargando…" })}
              />
            </Box>
          ) : marginError ? (
            <Typography color="error">
              {t("common.errorLoading", "Error loading data")}
            </Typography>
          ) : marginPeriods && marginPeriods.length > 0 ? (
            <MarginTable
              internalCampaigns={[
                {
                  id: internalCampaignId || "",
                  name: t("metrics.marginDetails", "Margin Details"),
                  margins: marginPeriods,
                },
              ]}
              hideInternalCampaignColumn={true}
              showViewButton={false}
            />
          ) : (
            <Typography variant="body1">
              {t(
                "metrics.noInternalCampaignMarginData",
                "No margin data available for this internal campaign."
              )}
            </Typography>
          )}

          <Divider sx={{ my: 4 }} />

          {campaignError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t(
                "errors.failedToLoadCampaign",
                "Failed to load campaign details"
              )}
            </Alert>
          ) : (
            <InternalCampaignDbMarginTable
              rawMargins={rawMargins || []}
              isLoading={isLoadingRawMargins || isLoadingCampaign}
              error={rawMarginError}
              internalCampaignId={internalCampaignId || ""}
              startDate={campaignStartDate}
              endDate={campaignEndDate}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InternalCampaignMargins;
